/**
 * useResumableSessions — surfaces any in-progress games discoverable from
 * localStorage, so a player who closes their tab can resume without losing
 * their seat.
 *
 * The wsClient persists a token per session at key `qd_token_${sessionId}`.
 * We scan those keys on mount and, optionally, ping the server to confirm
 * each session still exists. If the server says 404 we evict the token so
 * the home page doesn't keep advertising dead games.
 */

import { useCallback, useEffect, useState } from 'react';

import { apiFetch } from '../net/apiFetch';

const TOKEN_PREFIX = 'qd_token_';

export interface ResumableSession {
  readonly sessionId: string;
  /** `lobby` while the session is gathering players; `playing` after BEGIN. */
  readonly phase: 'lobby' | 'playing' | 'unknown';
  /** When we last saw this token used (ms since epoch). */
  readonly lastSeenAt: number;
}

function listStoredSessionIds(): string[] {
  if (typeof window === 'undefined') return [];
  const out: string[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(TOKEN_PREFIX)) {
        out.push(key.slice(TOKEN_PREFIX.length));
      }
    }
  } catch {
    // localStorage may be disabled (Safari private mode).
  }
  return out;
}

function evictToken(sessionId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(TOKEN_PREFIX + sessionId);
  } catch {
    /* noop */
  }
}

function getServerBaseUrl(): string {
  // We mirror wsClient's VITE_WS_BASE_URL but strip the protocol prefix so
  // we can hit `/api/...` over plain HTTP on the same host.
  const env = (import.meta as unknown as { env?: { VITE_WS_BASE_URL?: string } }).env;
  const wsBase = env?.VITE_WS_BASE_URL ?? 'ws://localhost:8787';
  return wsBase.replace(/^ws/, 'http');
}

export function useResumableSessions(): {
  sessions: readonly ResumableSession[];
  loading: boolean;
  forget: (sessionId: string) => void;
} {
  const [sessions, setSessions] = useState<readonly ResumableSession[]>([]);
  const [loading, setLoading] = useState(true);

  const forget = useCallback((sessionId: string) => {
    evictToken(sessionId);
    setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const ids = listStoredSessionIds();
    if (ids.length === 0) {
      setLoading(false);
      return;
    }

    const base = getServerBaseUrl();
    Promise.all(
      ids.map(async (sessionId): Promise<ResumableSession | null> => {
        try {
          // apiFetch retries transient failures so a cold-start Worker
          // doesn't show "Failed to fetch" on the home page.
          const res = await apiFetch(`${base}/api/sessions/${encodeURIComponent(sessionId)}`, {
            method: 'GET',
            // Resume probe is best-effort; cap retries to keep Home page snappy.
            maxAttempts: 2,
            timeoutMs: 4000,
          });
          if (res.status === 404) {
            evictToken(sessionId);
            return null;
          }
          if (!res.ok) {
            // Treat ambiguous errors as "still resumable" — the server may
            // be temporarily down, no reason to evict the token.
            return { sessionId, phase: 'unknown', lastSeenAt: Date.now() };
          }
          const body: { phase?: string } = await res.json();
          const phase: ResumableSession['phase'] =
            body.phase === 'playing' || body.phase === 'lobby' ? body.phase : 'unknown';
          return { sessionId, phase, lastSeenAt: Date.now() };
        } catch {
          // Network error → still resumable, optimistically.
          return { sessionId, phase: 'unknown', lastSeenAt: Date.now() };
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      setSessions(results.filter((r): r is ResumableSession => r !== null));
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return { sessions, loading, forget };
}
